import React, { useEffect, useState } from 'react';
import { Loader2, RotateCw } from 'lucide-react';
import type { GoogleCalendarDTO } from '../../global';
import type { ToastKind } from '../../components/Toast';
import { Button } from '../../components/ui/Button';
import { Dialog } from '../../components/ui/Dialog';
import { cn } from '../../components/ui/cn';
import { useLanguage } from '../../lib/i18n';

interface CalendarPickerDialogProps {
  open: boolean;
  onClose: () => void;
  /** Kayıttan sonra güncel liste — satırdaki "N takvim" sayısı tazelensin. */
  onSaved: (calendars: GoogleCalendarDTO[]) => void;
  onNotify: (kind: ToastKind, text: string) => void;
}

/**
 * Ayarlar > Takvim > Google Takvim > Yönet: hangi Google takvimlerinin
 * okunacağı. Toplantı listesi, otomatik bot ve hatırlatıcılar seçili
 * takvimlerin hepsini görür. En az bir takvim seçili kalmalı.
 */
export const CalendarPickerDialog: React.FC<CalendarPickerDialogProps> = ({ open, onClose, onSaved, onNotify }) => {
  const { t } = useLanguage();
  const [calendars, setCalendars] = useState<GoogleCalendarDTO[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // Her açılışta taze liste — Google tarafında takvim eklenmiş/çıkarılmış olabilir.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setCalendars(null);
    setLoadError(null);
    window.zabit
      .listGoogleCalendars()
      .then((list) => {
        if (!alive) return;
        setCalendars(list);
        setSelected(new Set(list.filter((calendar) => calendar.selected).map((calendar) => calendar.id)));
      })
      .catch((err: Error) => {
        if (alive) setLoadError(err.message || t('Takvim listesi alınamadı.', 'Could not load your calendars.'));
      });
    return () => {
      alive = false;
    };
  }, [open, attempt]);

  const toggle = (id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        // Son seçili takvim kaldırılamaz.
        if (next.size === 1) return current;
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const isUnchanged =
    calendars !== null &&
    calendars.every((calendar) => calendar.selected === selected.has(calendar.id));

  const save = async () => {
    setIsSaving(true);
    try {
      const saved = await window.zabit.setGoogleCalendars([...selected]);
      onSaved(saved);
      onNotify('success', t('Takvim seçimi kaydedildi.', 'Calendar selection saved.'));
      onClose();
    } catch (err) {
      onNotify('error', (err as Error).message || t('Takvim seçimi kaydedilemedi.', 'Could not save the calendar selection.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('Senkronize edilecek takvimler', 'Calendars to sync')}
      description={t(
        'Seçtiğin takvimlerdeki toplantılar listende görünür; otomatik bot ve hatırlatıcılar da onlara bakar.',
        'Meetings from the calendars you pick show up in your list, and the notetaker and reminders use them too.'
      )}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            {t('Vazgeç', 'Cancel')}
          </Button>
          <Button onClick={() => void save()} disabled={isSaving || !calendars || isUnchanged || selected.size === 0}>
            {isSaving && <Loader2 className="size-4 animate-spin" />}
            {t('Kaydet', 'Save')}
          </Button>
        </>
      }
    >
      {loadError ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-destructive">{loadError}</p>
          <Button variant="outline" size="sm" onClick={() => setAttempt((value) => value + 1)}>
            <RotateCw className="size-4" />
            {t('Tekrar dene', 'Try again')}
          </Button>
        </div>
      ) : !calendars ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {t('Takvimler yükleniyor…', 'Loading your calendars…')}
        </p>
      ) : (
        <ul className="-mx-2 space-y-0.5">
          {calendars.map((calendar) => {
            const isChecked = selected.has(calendar.id);
            const isLast = isChecked && selected.size === 1;
            return (
              <li key={calendar.id}>
                <label
                  className={cn(
                    'flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-accent/60',
                    isLast && 'cursor-not-allowed'
                  )}
                  title={isLast ? t('En az bir takvim seçili kalmalı.', 'Keep at least one calendar selected.') : undefined}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={isLast}
                    onChange={() => toggle(calendar.id)}
                    className="size-4 shrink-0 accent-brand"
                  />
                  <span
                    className="size-3 shrink-0 rounded-full border border-border"
                    style={{ backgroundColor: calendar.color ?? 'transparent' }}
                  />
                  <span className="min-w-0 flex-1 truncate text-sm">{calendar.name}</span>
                  {calendar.primary && (
                    <span className="shrink-0 rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                      {t('Ana takvim', 'Primary')}
                    </span>
                  )}
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </Dialog>
  );
};
